import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    ButtonBase,
    Chip,
    Drawer,
    IconButton,
    LinearProgress,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { answerLabel, schoolApi } from '../../common/schoolApi';
import { StudentPaperDialog } from '../assignment/StudentPaperDialog';
import { EmptyNote, LatestTest, Panel, RecentTest } from './schoolDashboard';

/**
 * LATEST TEST -- one closed test, in enough detail to act on.
 *
 * The dashboard payload only names the test (`latest_test`, `recent_tests`).
 * Everything else comes from the two endpoints the assignment screen already
 * uses -- /assignment/results (every student's mark) and /assignment/analysis
 * (question by question) -- so the numbers here and on that screen are the
 * same numbers, and the teacher's section fence is applied by the API.
 *
 * Averages are over students who SAT it. Missed students are counted beside
 * it, not folded in as zeros.
 */

interface ResultStudent {
    user_id: number;
    full_name: string | null;
    roll_number: string | null;
    section: string | null;
    state: string;
    percentage: number | null;
    correct: number | null;
    incorrect: number | null;
    unanswered: number | null;
    time_taken: number | null;
    rank: number | null;
}

interface Question {
    question_order: number;
    question_id: number;
    question_type: string;
    correct_answer: string;
    correct: number;
    answered: number;
    sat: number;
    correct_pct: number;
    answers: Array<{ answer: string; count: number; is_key: boolean }>;
    flags: string[];
    content: { question_text?: string | null; chapter_name?: string | null } | null;
}

const SAT = ['submitted', 'auto_submitted'];
const isSat = (s: ResultStudent) => SAT.includes(s.state) && s.percentage !== null;

/** Score bands, the same five the results screen uses. Negative marks land in the first. */
const SCORE_BANDS = [
    { key: '0-20', label: 'Under 20%', min: -Infinity, max: 20, tone: 'error' as const },
    { key: '20-40', label: '20–40%', min: 20, max: 40, tone: 'error' as const },
    { key: '40-60', label: '40–60%', min: 40, max: 60, tone: 'warning' as const },
    { key: '60-80', label: '60–80%', min: 60, max: 80, tone: 'success' as const },
    { key: '80-100', label: '80% and up', min: 80, max: Infinity, tone: 'success' as const },
];
type Filter = 'all' | 'missed' | 'low' | string; // or a SCORE_BANDS key
const LOW_BELOW = 40;

const bandKeyOf = (pct: number) => SCORE_BANDS.find(b => pct >= b.min && pct < b.max)?.key || '0-20';

const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${Math.round(v * 10) / 10}%`);

const avg = (xs: number[]) => (xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : null);

const plain = (html?: string | null) =>
    (html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const clock = (s: number | null) => (s === null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);

const shortDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso.replace(' ', 'T'));
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const STATE_LABEL: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
    submitted: { label: 'Submitted', color: 'success' },
    auto_submitted: { label: 'Timed out', color: 'info' },
    missed: { label: "Didn't sit", color: 'error' },
    excused: { label: 'Excused', color: 'default' },
    in_progress: { label: 'In progress', color: 'warning' },
    assigned: { label: 'Not started', color: 'default' },
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

const ScoreSpread = ({ students, onSelect }: { students: ResultStudent[]; onSelect: (key: string) => void }) => {
    const theme = useTheme();
    const counts = SCORE_BANDS.map(b => ({
        ...b,
        n: students.filter(s => isSat(s) && bandKeyOf(s.percentage as number) === b.key).length,
    }));
    const max = Math.max(1, ...counts.map(c => c.n));

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Score spread
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 110 }}>
                {counts.map(c => (
                    <Tooltip key={c.key} title={`${c.label}: ${c.n} student${c.n === 1 ? '' : 's'} · click to see who`}>
                        <ButtonBase
                            disabled={c.n === 0}
                            onClick={() => onSelect(c.key)}
                            sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', borderRadius: 1 }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {c.n}
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: `${Math.max(4, (c.n / max) * 80)}px`,
                                    borderRadius: '4px 4px 0 0',
                                    bgcolor: alpha(theme.palette[c.tone].main, 0.75),
                                    '&:hover': { bgcolor: theme.palette[c.tone].main },
                                }}
                            />
                        </ButtonBase>
                    </Tooltip>
                ))}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                {counts.map(c => (
                    <Typography key={c.key} variant="caption" color="text.secondary" align="center" sx={{ flex: 1, lineHeight: 1.2 }}>
                        {c.key === '80-100' ? '80+' : c.key === '0-20' ? '<20' : c.key}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
};

const SectionRows = ({ students, onSelect }: { students: ResultStudent[]; onSelect: (section: string) => void }) => {
    const rows = useMemo(() => {
        const by = new Map<string, ResultStudent[]>();
        students.forEach(s => {
            const k = s.section || '—';
            by.set(k, [...(by.get(k) || []), s]);
        });
        return Array.from(by.entries())
            .map(([section, list]) => {
                const counted = list.filter(s => s.state !== 'excused');
                const sat = counted.filter(isSat);
                return {
                    section,
                    assigned: counted.length,
                    submitted: sat.length,
                    missed: list.filter(s => s.state === 'missed').length,
                    average: avg(sat.map(s => s.percentage as number)),
                };
            })
            .sort((a, b) => a.section.localeCompare(b.section));
    }, [students]);

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Submitted</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Missed</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Average</TableCell>
                    <TableCell padding="none" />
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map(r => (
                    <TableRow key={r.section} hover onClick={() => onSelect(r.section)} sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell>
                            <Chip size="small" variant="outlined" label={r.section} />
                        </TableCell>
                        <TableCell align="right">
                            {r.submitted} / {r.assigned}
                        </TableCell>
                        <TableCell align="right">
                            {r.missed > 0 ? (
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                                    {r.missed}
                                </Typography>
                            ) : (
                                0
                            )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 700, width: 44 }}>
                                    {pct(r.average)}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.max(0, Math.min(100, r.average ?? 0))}
                                    color={r.average === null ? 'inherit' : r.average >= 60 ? 'success' : r.average >= 40 ? 'warning' : 'error'}
                                    sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                />
                            </Stack>
                        </TableCell>
                        <TableCell padding="none" sx={{ width: 24 }}>
                            <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

const HardestQuestions = ({ questions, onSeeAll }: { questions: Question[]; onSeeAll: () => void }) => {
    const hardest = [...questions].sort((a, b) => a.correct_pct - b.correct_pct).slice(0, 5);

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Hardest questions
                </Typography>
                <Button size="small" onClick={onSeeAll} endIcon={<ChevronRightIcon />}>
                    All {questions.length}
                </Button>
            </Stack>
            {hardest.length === 0 ? (
                <EmptyNote>Nobody has submitted yet.</EmptyNote>
            ) : (
                <Stack spacing={1.25}>
                    {hardest.map(q => {
                        const topWrong = q.answers.filter(a => !a.is_key).sort((a, b) => b.count - a.count)[0];
                        const text = plain(q.content?.question_text);
                        return (
                            <Box key={q.question_order}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                                        <b>Q{q.question_order}</b>
                                        {q.content?.chapter_name ? (
                                            <Typography component="span" variant="caption" color="text.secondary">
                                                {' '}· {q.content.chapter_name}
                                            </Typography>
                                        ) : null}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0, color: q.correct_pct < 30 ? 'error.main' : q.correct_pct < 50 ? 'warning.main' : 'text.primary' }}>
                                        {q.correct_pct}% right
                                    </Typography>
                                </Stack>
                                {text && (
                                    <Tooltip title={text}>
                                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                                            {text}
                                        </Typography>
                                    </Tooltip>
                                )}
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, q.correct_pct)}
                                    color={q.correct_pct < 30 ? 'error' : q.correct_pct < 50 ? 'warning' : 'success'}
                                    sx={{ height: 5, borderRadius: 3, my: 0.5 }}
                                />
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                    {topWrong && topWrong.count > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Most common wrong answer: <b>{answerLabel(topWrong.answer, q.question_type)}</b> ({topWrong.count}) · key{' '}
                                            <b>{answerLabel(q.correct_answer, q.question_type)}</b>
                                        </Typography>
                                    )}
                                    {q.flags.includes('check_key') && (
                                        <Tooltip title="A wrong answer was chosen more often than the key. The key or the wording may be wrong.">
                                            <Chip size="small" color="warning" label="Check the key" sx={{ height: 18, fontSize: 10 }} />
                                        </Tooltip>
                                    )}
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
};

const TestStudentsDrawer = ({
    open,
    onClose,
    assignmentId,
    title,
    students,
    initialFilter,
    initialSection,
}: {
    open: boolean;
    onClose: () => void;
    assignmentId: number;
    title: string;
    students: ResultStudent[];
    initialFilter: Filter;
    initialSection: string;
}) => {
    const theme = useTheme();
    const [filter, setFilter] = useState<Filter>(initialFilter);
    const [section, setSection] = useState(initialSection);
    const [search, setSearch] = useState('');
    const [paper, setPaper] = useState<number | null>(null);

    useEffect(() => {
        if (open) {
            setFilter(initialFilter);
            setSection(initialSection);
            setSearch('');
        }
    }, [open, initialFilter, initialSection]);

    const sections = Array.from(new Set(students.map(s => s.section || '—'))).sort();
    const inSection = students.filter(s => !section || (s.section || '—') === section);
    const matches = (s: ResultStudent, f: Filter) =>
        f === 'all'
            ? true
            : f === 'missed'
            ? s.state === 'missed'
            : f === 'low'
            ? isSat(s) && (s.percentage as number) < LOW_BELOW
            : isSat(s) && bandKeyOf(s.percentage as number) === f;

    const q = search.trim().toLowerCase();
    const shown = inSection
        .filter(s => matches(s, filter))
        .filter(s => !q || `${s.full_name || ''} ${s.roll_number || ''}`.toLowerCase().includes(q))
        // Didn't sit first, then lowest score first.
        .sort((a, b) => (isSat(a) ? 1 : 0) - (isSat(b) ? 1 : 0) || (a.percentage ?? -999) - (b.percentage ?? -999));

    const chips: Array<{ key: Filter; label: string }> = [
        { key: 'all', label: 'All' },
        { key: 'missed', label: "Didn't sit" },
        { key: 'low', label: `Below ${LOW_BELOW}%` },
        ...SCORE_BANDS.map(b => ({ key: b.key, label: b.label })),
    ];

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 680 }, maxWidth: '100%', display: 'flex', flexDirection: 'column' } }}>
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Students who didn't sit it first, then lowest score first. Click a student to see their paper.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Stack direction="row" sx={{ mt: 2, flexWrap: 'wrap', gap: 0.75 }}>
                    {chips.map(c => {
                        const n = inSection.filter(s => matches(s, c.key)).length;
                        return (
                            <Chip
                                key={c.key}
                                size="small"
                                label={`${c.label} · ${n}`}
                                color={filter === c.key ? 'primary' : 'default'}
                                variant={filter === c.key ? 'filled' : 'outlined'}
                                onClick={() => setFilter(c.key)}
                            />
                        );
                    })}
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
                    <TextField select size="small" label="Section" value={section} onChange={e => setSection(e.target.value)} fullWidth={false} sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0 }}>
                        <MenuItem value="">All sections</MenuItem>
                        {sections.map(s => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField size="small" label="Search name or roll" value={search} onChange={e => setSearch(e.target.value)} fullWidth={false} sx={{ flex: 1, minWidth: 180 }} />
                </Stack>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pb: 2 }}>
                {shown.length === 0 ? (
                    <Box sx={{ pt: 2 }}>
                        <EmptyNote>No students match.</EmptyNote>
                    </Box>
                ) : (
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">Score</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">
                                    <Tooltip title="Right / wrong / blank">
                                        <span>R / W / B</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="right">Time</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.map(s => {
                                const st = STATE_LABEL[s.state] || { label: s.state, color: 'default' as const };
                                const sat = isSat(s);
                                return (
                                    <TableRow key={s.user_id} hover={sat} onClick={sat ? () => setPaper(s.user_id) : undefined} sx={{ cursor: sat ? 'pointer' : 'default' }}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {s.full_name || `Student ${s.user_id}`}
                                            </Typography>
                                            {s.roll_number && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Roll {s.roll_number}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{s.section || '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" variant="outlined" color={st.color} label={st.label} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: !sat ? 'text.secondary' : (s.percentage as number) < 40 ? 'error.main' : (s.percentage as number) < 60 ? 'warning.main' : 'success.main' }}>
                                                {sat ? pct(s.percentage) : '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption">{sat ? `${s.correct ?? 0} / ${s.incorrect ?? 0} / ${s.unanswered ?? 0}` : '—'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption">{sat ? clock(s.time_taken) : '—'}</Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Box>
            {paper !== null && <StudentPaperDialog assignmentId={assignmentId} userId={paper} onClose={() => setPaper(null)} />}
        </Drawer>
    );
};

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export const LatestTestPanel = ({ test, recent }: { test?: LatestTest | null; recent?: RecentTest[] }) => {
    const navigate = useNavigate();
    const [id, setId] = useState<number | null>(test?.assignment_id ?? null);
    const [results, setResults] = useState<{ id: number; students: ResultStudent[] } | null>(null);
    const [questions, setQuestions] = useState<Question[] | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const [drill, setDrill] = useState<{ open: boolean; filter: Filter; section: string }>({ open: false, filter: 'all', section: '' });

    useEffect(() => {
        if (id === null) return;
        let cancelled = false;
        setFailed(null);
        setQuestions(null);
        Promise.all([schoolApi.get(`assignment/results/${id}`), schoolApi.get(`assignment/analysis/${id}`)])
            .then(([r, a]) => {
                if (cancelled) return;
                setResults({ id, students: r.result.students || [] });
                setQuestions(a.result.questions || []);
            })
            .catch(e => !cancelled && setFailed(e?.message || 'Could not load the results.'));
        return () => {
            cancelled = true;
        };
    }, [id]);

    const picked = (recent || []).find(r => r.assignment_id === id);
    const title = picked?.title || test?.title || 'Test';
    const closed = picked?.scheduled_end || test?.scheduled_end || null;
    const subject = picked?.subject ?? test?.subject ?? null;

    const students = results && results.id === id ? results.students : null;
    const sat = (students || []).filter(isSat);
    const scores = sat.map(s => s.percentage as number);
    const counted = (students || []).filter(s => s.state !== 'excused');
    const missed = (students || []).filter(s => s.state === 'missed').length;
    const lowCount = sat.filter(s => (s.percentage as number) < LOW_BELOW).length;

    const openDrill = (filter: Filter, section = '') => setDrill({ open: true, filter, section });
    const goTo = (tab: 'results' | 'analysis') => id !== null && navigate(`/assignment/${id}/show?tab=${tab}`);

    return (
        <Panel
            title="Latest test"
            tone="info"
            icon={<AssessmentIcon fontSize="small" />}
            subtitle={
                id !== null
                    ? `${recent && recent.length > 1 ? (subject ? `${subject} · ` : '') : `${title} · `}closed ${shortDate(closed)}. Click a bar, a section or a count to see the students.`
                    : 'Your most recent test, once its window has closed.'
            }
            action={
                recent && recent.length > 1 ? (
                    <TextField
                        select
                        size="small"
                        value={id ?? ''}
                        onChange={e => setId(Number(e.target.value))}
                        fullWidth={false}
                        margin="none"
                        sx={{ width: { xs: 180, sm: 280 } }}
                    >
                        {recent.map(r => (
                            <MenuItem key={r.assignment_id} value={r.assignment_id}>
                                {r.title} · {shortDate(r.scheduled_end)}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : subject ? (
                    <Chip size="small" label={subject} />
                ) : undefined
            }
        >
            {id === null ? (
                <EmptyNote>You have no closed test yet. Results appear here once a test you set has ended.</EmptyNote>
            ) : failed ? (
                <EmptyNote>{failed}</EmptyNote>
            ) : !students ? (
                <LinearProgress sx={{ my: 3 }} />
            ) : (
                <>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {[
                                { label: 'Average score', value: pct(avg(scores)) },
                                { label: 'Submitted', value: `${sat.length} / ${counted.length}` },
                                { label: "Didn't sit", value: missed, onClick: missed > 0 ? () => openDrill('missed') : undefined, color: missed > 0 ? 'error.main' : undefined },
                                { label: `Below ${LOW_BELOW}%`, value: lowCount, onClick: lowCount > 0 ? () => openDrill('low') : undefined, color: lowCount > 0 ? 'warning.main' : undefined },
                                { label: 'Highest', value: pct(scores.length ? Math.max(...scores) : null) },
                                { label: 'Lowest', value: pct(scores.length ? Math.min(...scores) : null) },
                            ].map(s => (
                                <ButtonBase key={s.label} disabled={!s.onClick} onClick={s.onClick} sx={{ display: 'block', textAlign: 'left', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {s.label}
                                        {s.onClick ? ' ›' : ''}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: s.color }}>
                                        {s.value}
                                    </Typography>
                                </ButtonBase>
                            ))}
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button onClick={() => openDrill('all')}>Every student</Button>
                            <Button onClick={() => goTo('results')} endIcon={<ChevronRightIcon />}>
                                Full results
                            </Button>
                        </Stack>
                    </Stack>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        <Box sx={{ flex: '1 1 260px', minWidth: 240 }}>
                            <ScoreSpread students={students} onSelect={key => openDrill(key)} />
                        </Box>
                        <Box sx={{ flex: '2 1 380px', minWidth: 300 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                By section
                            </Typography>
                            <SectionRows students={students} onSelect={section => openDrill('all', section)} />
                        </Box>
                        <Box sx={{ flex: '2 1 340px', minWidth: 280 }}>
                            {questions === null ? <LinearProgress /> : <HardestQuestions questions={questions} onSeeAll={() => goTo('analysis')} />}
                        </Box>
                    </Box>

                    <TestStudentsDrawer
                        open={drill.open}
                        onClose={() => setDrill(d => ({ ...d, open: false }))}
                        assignmentId={id}
                        title={title}
                        students={students}
                        initialFilter={drill.filter}
                        initialSection={drill.section}
                    />
                </>
            )}
        </Panel>
    );
};

export default LatestTestPanel;
